// pattern: Imperative Shell
// Landing page view — entry point for teachers and students

import { createElement, addListener } from '../shell/dom.ts';
import { createDashboard } from '../shell/api-client.ts';
import { navigate, routeToHash } from '../shell/router.ts';

export function renderLanding(container: HTMLElement): () => void {
  const cleanups: Array<() => void> = [];

  // Header
  const h1 = createElement('h1', { textContent: 'KeyQuiz', className: 'landing-title' });

  const subtitle = createElement('p', {
    textContent: 'Turn PDFs into interactive quizzes with AI',
    className: 'text-secondary landing-subtitle',
  });

  const header = createElement('header', { className: 'text-center' });
  header.append(h1, subtitle);

  // Create quiz card
  const createCard = createElement('div', { className: 'card landing-create-card' });

  const createTitle = createElement('h2', {
    textContent: 'Create a Quiz',
    className: 'landing-section-title',
  });

  const createDesc = createElement('p', {
    textContent: 'Upload a PDF and let AI generate multiple-choice questions.',
    className: 'text-secondary landing-section-desc',
  });

  const createBtn = createElement('button', {
    className: 'btn btn-primary landing-btn-full',
    textContent: 'Get Started',
  });

  createCard.append(createTitle, createDesc, createBtn);

  // Dashboard link card
  const dashCard = createElement('div', { className: 'card' });

  const dashTitle = createElement('h2', {
    textContent: 'Access Your Dashboard',
    className: 'landing-section-title',
  });

  const dashDesc = createElement('p', {
    textContent: 'Have a dashboard link? Paste it below to manage your quizzes.',
    className: 'text-secondary landing-dash-desc',
  });

  const dashInput = createElement('input', {
    className: 'input landing-dash-input',
    placeholder: 'Paste dashboard link or ID...',
  });

  const dashBtn = createElement('button', {
    className: 'btn btn-secondary landing-btn-full',
    textContent: 'Go to Dashboard',
  });

  dashCard.append(dashTitle, dashDesc, dashInput, dashBtn);

  // Append everything
  container.append(header, createCard, dashCard);

  // Event handlers
  const createCleanup = addListener(createBtn, 'click', async () => {
    createBtn.textContent = 'Creating...';
    createBtn.setAttribute('disabled', '');

    const result = await createDashboard();
    if (result.ok) {
      navigate(routeToHash({ type: 'dashboard', dashboardId: result.data.id }));
    } else {
      createBtn.textContent = 'Get Started';
      createBtn.removeAttribute('disabled');
      // Show error inline
      const errorMsg = createElement('p', {
        textContent: result.error || 'Failed to create dashboard',
        className: 'landing-error',
      });
      createCard.append(errorMsg);
    }
  });
  cleanups.push(createCleanup);

  const dashCleanup = addListener(dashBtn, 'click', () => {
    const value = dashInput.value.trim();
    // Extract dashboard ID from URL or use raw ID
    const idMatch = value.match(/[#/]t\/([A-Za-z0-9]{8})/);
    const id = idMatch ? idMatch[1] : value;

    if (id && /^[A-Za-z0-9]{8}$/.test(id)) {
      navigate(routeToHash({ type: 'dashboard', dashboardId: id }));
    } else {
      dashInput.style.borderColor = 'var(--color-error)';
    }
  });
  cleanups.push(dashCleanup);

  // Enter key on input
  const inputCleanup = addListener(dashInput, 'keydown', (e) => {
    if ((e as KeyboardEvent).key === 'Enter') {
      dashBtn.click();
    }
  });
  cleanups.push(inputCleanup);

  return () => cleanups.forEach((fn) => fn());
}
